import { GlobalStyles } from '@experiences/theme';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, {
    useCallback,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        showMoreButton: ({ size }: { size: 'small' | 'standard' }) => ({
            // Small sizing matches ApButton small sizing
            width: size === 'small' ? '32px' : '36px',
            height: size === 'small' ? '32px' : '36px',
            padding: '8px',
            color: theme.palette.semantic.colorIconDefault,
        }),
        menuItem: {
            '&:focus': { backgroundColor: theme.palette.semantic.colorHover },
            '&:hover': { backgroundColor: theme.palette.semantic.colorHover },
        },
    }),
}));

export interface ICardAction {
    label: string | (() => string);
    click: () => void;
    disable?: boolean;
    invisible?: boolean | (() => boolean);
    dataCy: string;
    ariaLabel: string | (() => string);
}

interface IMoreRowActionsProps {
    actions: ICardAction[];
    disabled: boolean | undefined;
    triggerButtonSize?: 'small' | 'standard';
}

export const UiMoreActionsComponent = ({
    actions, disabled, triggerButtonSize = 'standard',
}: IMoreRowActionsProps) => {
    const classes = useStyles({ size: triggerButtonSize });
    const { formatMessage: translate } = useIntl();

    const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>(null);

    const clickMenuItem = useCallback((click: () => void) => {
        setAnchorEl(null);
        click();
    }, []);

    return (
        <>
            <Tooltip title={translate({ id: 'CLIENT_SHOW_MORE_ACTIONS' })}>
                <IconButton
                    aria-label={translate({ id: 'CLIENT_SHOW_MORE_ACTIONS' })}
                    className={classes.showMoreButton}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                        setAnchorEl(event.currentTarget);
                    }}
                    data-cy="ui-grid-show-more-actions-button"
                >
                    <MoreVertIcon className={classes.icon} />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                data-cy="ui-grid-show-more-actions-menu"
            >
                {actions.map((action, p) =>
                    (
                        (action.invisible !== undefined
                            && (typeof action.invisible === 'function' ?
                                action.invisible()
                                : action.invisible
                            )
                        ) ? null :
                            <MenuItem
                                key={p}
                                disabled={action?.disable ?? disabled}
                                data-cy={action.dataCy}
                                className={classes.menuItem}
                                aria-labelledby={typeof action.ariaLabel === 'function' ? action.ariaLabel() : action.ariaLabel}
                                onClick={() => (action.click ? clickMenuItem(action.click) : undefined)}
                            >
                                <ListItemText
                                    primary={typeof action.label === 'function' ? action.label() : action.label}
                                />
                            </MenuItem>
                    ),
                )}
            </Menu>
        </>
    );
};
